import { useQuery } from '@tanstack/react-query';

import { useAuthentication } from '../Idp/useAuthentication';
import { useCoreApi } from './api';
import { User } from './user/users.types';

interface Role {
  name: string;
  uuid: string;
  deleted: boolean;
  pending: boolean;
  deleted_at: string;
  created_at: string;
  updated_at: string;
  access: Access;
  root: string;
}

interface Access {
  backer_tracker: Permissions;
  cause: Permissions;
  dashboard: Permissions;
  owner: Permissions;
  payouts: Permissions;
  promotion_center: Permissions;
  fee: Permissions;
  role: Permissions;
  wallet: Permissions;
  import: Omit<Permissions, 'read'>;
  cart: Permissions;
  dues_payment: Permissions;
  member: Permissions;
  fundraiser: Permissions;
  store_item: Permissions;
  subscription: Permissions;
  compliance: Permissions;
  form: Permissions;
  officer: Permissions;
  officer_type: Permissions;
  submission: Permissions;
  communication: Permissions;
  // access to contacts feature
  backer: Permissions;
  account_code: Permissions;
  assign_account_code: Permissions;
  pos_cart: Permissions;
  sales_tax: Permissions;
}

interface Permissions {
  write: boolean;
  read: boolean;
  manage?: boolean;
}

interface UserWithRole extends User {
  role: Role;
}

type UserRoleResponse = {
  user: UserWithRole;
};

const useUserRole = <T = UserWithRole>({
  select,
  causeId,
}: {
  causeId?: string;
  select?: (data: UserWithRole) => T;
}) => {
  const { isAuthenticated } = useAuthentication();
  const causeQuery = `cause_id=${causeId}`;
  const CoreApi = useCoreApi();

  const getUserRole = async () => {
    const { data } = await CoreApi.get<UserRoleResponse>(`/users/me?${causeQuery}`);
    return data.user;
  };

  return useQuery({
    queryKey: ['user', 'role', causeId],
    queryFn: getUserRole,
    enabled: isAuthenticated && !!causeId,
    select,
  });
};

/** Returns the targeted permissions */
const useUserAccessPermissions = <TAccessType extends keyof Role['access']>({
  accessType,
  causeId,
}: {
  accessType: TAccessType;
  causeId?: string;
}) => {
  return useUserRole({
    causeId,
    select(data) {
      return data.role?.access?.[accessType];
    },
  });
};

export { useUserAccessPermissions, useUserRole };
